import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useUserColors } from '../../context/UserColorsContext';
import { initials, textOn, ColorDot } from '../common/PersonColor';

// Vacations and absences of one day as compact chips: a circle with the
// person's initials (vacation: their colour; absence: grey, dashed border).
// Up to `max` chips, then "+N". Tapping (or hovering) opens a small list.
// Chips sit in normal flow, so they never overlap shift cards or the date.

const ABSENCE_LABEL = { unpaid_leave: 'Unpaid leave' };
const absenceLabel = (t) => ABSENCE_LABEL[t] || String(t).replace(/_/g, ' ');

export function dayPeopleCount(entry) {
  return (entry?.vacation?.length || 0) + (entry?.absent?.length || 0);
}

function Chip({ person, absent, size, colorOf }) {
  const bg = absent ? '#F1F5F9' : colorOf(person.user_id);
  return (
    <span
      aria-hidden="true"
      style={{
        width: size, height: size, minWidth: size, borderRadius: '50%', boxSizing: 'border-box',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: Math.max(8, Math.round(size * 0.46)), fontWeight: 700, lineHeight: 1, letterSpacing: '-0.02em',
        background: bg, color: absent ? '#475569' : textOn(bg),
        border: absent ? '1.5px dashed #64748B' : 'none',
        boxShadow: absent ? 'none' : 'inset 0 0 0 1px rgba(0,0,0,0.15)',
      }}
    >
      {initials(person.name)}
    </span>
  );
}

export default function DayPeople({ entry, max = 3, size = 18, align = 'flex-start', dateLabel }) {
  const { colorOf } = useUserColors();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const hoverTimer = useRef(null);

  const vacation = entry?.vacation || [];
  const absent = entry?.absent || [];
  const all = [...vacation.map((p) => ({ p, absent: false })), ...absent.map((p) => ({ p, absent: true }))];

  // Close on outside tap / Escape / scroll
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  if (all.length === 0) return null;

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = 220;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    const below = r.bottom + 4;
    setPos({ left, top: below, width });
  };
  const show = () => { place(); setOpen(true); };
  const hoverIn = (e) => {
    if (e.pointerType && e.pointerType !== 'mouse') return; // touch: tap only
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(show, 250);
  };
  const hoverOut = (e) => {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setOpen(false), 200);
  };

  const shown = all.slice(0, max);
  const more = all.length - shown.length;
  const summary = [
    vacation.length ? `On vacation: ${vacation.map((p) => p.name).join(', ')}` : null,
    absent.length ? `Absent: ${absent.map((p) => p.name).join(', ')}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <>
      {/* span, not button: FullCalendar renders day numbers / headers inside links.
          Pointer events stop here so a tap never also triggers the day click. */}
      <span
        ref={btnRef}
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); if (open) setOpen(false); else show(); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); if (open) setOpen(false); else show(); }
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onPointerEnter={hoverIn}
        onPointerLeave={hoverOut}
        aria-label={summary}
        aria-expanded={open}
        title={summary}
        style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: align, gap: '2px',
          background: 'none', border: 'none', padding: '1px 0', margin: 0, cursor: 'pointer', maxWidth: '100%',
        }}
      >
        {shown.map(({ p, absent: isAbsent }) => (
          <Chip key={`${isAbsent ? 'a' : 'v'}${p.user_id}`} person={p} absent={isAbsent} size={size} colorOf={colorOf} />
        ))}
        {more > 0 && (
          <span
            style={{
              height: size, minWidth: size, padding: '0 3px', borderRadius: size, boxSizing: 'border-box',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: Math.max(8, Math.round(size * 0.46)), fontWeight: 700, color: 'var(--primary-dark)',
              background: 'var(--primary-xlight)', border: '1px solid var(--primary-light)',
            }}
          >
            +{more}
          </span>
        )}
      </span>

      {open && pos && createPortal(
        <div
          ref={popRef}
          role="dialog"
          aria-label={dateLabel ? `People away on ${dateLabel}` : 'People away'}
          onPointerEnter={(e) => { if (!e.pointerType || e.pointerType === 'mouse') clearTimeout(hoverTimer.current); }}
          onPointerLeave={hoverOut}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', left: pos.left, top: pos.top, width: pos.width, zIndex: 1500,
            background: 'var(--white)', border: '1px solid var(--primary-light)', borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow-lg)', padding: '8px 10px', fontSize: '0.8rem', textAlign: 'left', color: 'var(--text)',
          }}
        >
          {dateLabel && <div style={{ fontWeight: 700, marginBottom: '4px' }}>{dateLabel}</div>}
          {vacation.length > 0 && (
            <div style={{ marginBottom: absent.length ? '6px' : 0 }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-light)', fontWeight: 600, marginBottom: '2px' }}>🌴 On vacation</div>
              {vacation.map((p) => (
                <div key={p.user_id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '1px 0' }}>
                  <ColorDot userId={p.user_id} size={9} /> {p.name}
                </div>
              ))}
            </div>
          )}
          {absent.length > 0 && (
            <div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-light)', fontWeight: 600, marginBottom: '2px' }}>Absent</div>
              {absent.map((p) => (
                <div key={p.user_id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '1px 0' }}>
                  <ColorDot userId={p.user_id} size={9} /> {p.name}
                  {p.absence_type && (
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>· {absenceLabel(p.absence_type)}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
